import React, { useEffect, useRef, useState } from 'react';
import './DeploymentUpdateToast.css';

const CHECK_INTERVAL_MS = 60_000;
const REQUEST_TIMEOUT_MS = 10_000;
const TOAST_DURATION_MS = 20_000;
const SEEN_BUILDS_KEY = 'intentico:seenDeploymentBuilds';

// Session storage can be unavailable in private browsing; keep the fallback per tab.
const seenBuilds = new Set<string>();

function hasSeenBuild(buildId: string): boolean {
  if (seenBuilds.has(buildId)) return true;
  try {
    const stored = JSON.parse(sessionStorage.getItem(SEEN_BUILDS_KEY) || '[]');
    if (Array.isArray(stored) && stored.includes(buildId)) {
      seenBuilds.add(buildId);
      return true;
    }
  } catch {
    // The in-memory set still prevents repeat notices in this tab.
  }
  return false;
}

function markSeen(buildId: string): void {
  seenBuilds.add(buildId);
  try {
    const stored = JSON.parse(sessionStorage.getItem(SEEN_BUILDS_KEY) || '[]');
    const ids = Array.isArray(stored) ? stored.filter((id): id is string => typeof id === 'string') : [];
    sessionStorage.setItem(SEEN_BUILDS_KEY, JSON.stringify([...new Set([...ids, buildId])]));
  } catch {
    // Storage is optional; the in-memory set covers the current tab.
  }
}

interface DeploymentUpdateToastProps {
  buildId?: string;
  enabled?: boolean;
  onRefresh?: () => void;
}

export function DeploymentUpdateToast({
  buildId = import.meta.env.VITE_BUILD_ID,
  enabled = import.meta.env.PROD,
  onRefresh = () => window.location.reload(),
}: DeploymentUpdateToastProps) {
  const [availableBuild, setAvailableBuild] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(document.hidden);
  const remainingMs = useRef(TOAST_DURATION_MS);
  const timedBuild = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !buildId) return;

    let disposed = false;
    let inFlight = false;
    let controller: AbortController | null = null;

    const check = async () => {
      if (disposed || inFlight || document.hidden || !navigator.onLine) return;
      inFlight = true;
      const request = new AbortController();
      controller = request;
      const timeout = window.setTimeout(() => request.abort(), REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch('/version.json', {
          cache: 'no-store',
          signal: request.signal,
        });
        if (!response.ok) return;
        const version: unknown = await response.json();
        if (disposed || request.signal.aborted) return;
        const latest = (version as { buildId?: unknown } | null)?.buildId;
        if (typeof latest !== 'string' || !latest.trim() || latest.length > 128) return;
        if (latest !== buildId && !hasSeenBuild(latest)) {
          markSeen(latest);
          remainingMs.current = TOAST_DURATION_MS;
          timedBuild.current = latest;
          setAvailableBuild(latest);
        }
      } catch {
        // An unavailable version file should not interrupt the app.
      } finally {
        window.clearTimeout(timeout);
        if (controller === request) controller = null;
        inFlight = false;
      }
    };

    const onVisibilityChange = () => {
      setHidden(document.hidden);
      if (!document.hidden) void check();
    };
    const onOnline = () => { void check(); };

    void check();
    const interval = window.setInterval(() => { void check(); }, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);
    return () => {
      disposed = true;
      controller?.abort();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
    };
  }, [buildId, enabled]);

  useEffect(() => {
    if (!availableBuild || hovered || focused || hidden) return;
    const startedAt = Date.now();
    const timeout = window.setTimeout(() => setAvailableBuild(null), remainingMs.current);
    return () => {
      window.clearTimeout(timeout);
      if (timedBuild.current === availableBuild) {
        remainingMs.current = Math.max(0, remainingMs.current - (Date.now() - startedAt));
      }
    };
  }, [availableBuild, hovered, focused, hidden]);

  if (!availableBuild) return null;

  return (
    <div
      className="deployment-update-toast"
      role="status"
      aria-live="polite"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
      }}
    >
      <span>There's a new version of Intentico available. Refresh to update.</span>
      <div className="deployment-update-actions">
        <button type="button" className="deployment-update-refresh" onClick={onRefresh}>
          Refresh
        </button>
        <button
          type="button"
          className="deployment-update-close"
          aria-label="Close update notice"
          onClick={() => {
            setFocused(false);
            setHovered(false);
            setAvailableBuild(null);
          }}
        >
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
